import React, { useEffect, useState } from 'react';
import { X, Send, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import UserAvatar from './UserAvatar';
import { relativeTime, renderCaption } from '../lib/social';
import { toast } from 'sonner';

export default function CommentSheet({ post, onClose, onCommentAdded }) {
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/posts/${post.post_id}/comments`);
        setItems(data.items || []);
      } catch (_e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [post.post_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.post_id}/comments`, { text, parent_comment_id: replyTo?.comment_id || null });
      setItems((prev) => [...prev, data]);
      setText('');
      setReplyTo(null);
      onCommentAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('toast.error'));
    } finally { setBusy(false); }
  };

  const del = async (c) => {
    try {
      await api.delete(`/comments/${c.comment_id}`);
      setItems((prev) => prev.filter((x) => x.comment_id !== c.comment_id));
    } catch (_e) { toast.error(t('toast.error')); }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="comments-sheet">
      <div className="mt-auto w-full max-h-[85vh] aura-glass rounded-t-2xl border-t border-white/10 flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="font-display font-bold text-white">{t('feed.comments')}</div>
          <button onClick={onClose} className="text-aura-textDim hover:text-white" data-testid="comments-close"><X size={20} /></button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {loading ? (
            <div className="text-center text-aura-textDim py-8 animate-aura-pulse text-2xl">⚡</div>
          ) : items.length === 0 ? (
            <div className="text-center text-sm text-aura-textDim py-10">{t('feed.noComments')}</div>
          ) : (
            items.map((c) => (
              <div key={c.comment_id} className={`flex gap-3 ${c.parent_comment_id ? 'pl-8' : ''}`} data-testid={`comment-${c.comment_id}`}>
                <UserAvatar user={c.author} size="xs" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-semibold text-white text-sm">@{c.author?.username}</span>
                    <span className="text-[10px] text-aura-textDim">{relativeTime(c.created_at, locale)}</span>
                    {(user?.user_id === c.user_id) && (
                      <button onClick={() => del(c)} className="ml-auto text-red-400/70 hover:text-red-400" data-testid={`del-comment-${c.comment_id}`}><Trash2 size={12} /></button>
                    )}
                  </div>
                  <div className="text-sm text-white/90 break-words">{renderCaption(c.text)}</div>
                  {user && !c.parent_comment_id && (
                    <button onClick={() => setReplyTo(c)} className="text-[10px] uppercase tracking-widest text-aura-textDim hover:text-aura-cyan mt-1">
                      {t('feed.reply')}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {user ? (
          <form onSubmit={submit} className="border-t border-white/10 px-3 py-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] flex items-center gap-2 bg-aura-surface/80">
            <UserAvatar user={user} size="xs" />
            <div className="flex-1">
              {replyTo && (
                <div className="text-[10px] text-aura-textDim mb-1">
                  {t('feed.replyingTo')} <span className="text-aura-cyan">@{replyTo.author?.username}</span>
                  <button type="button" onClick={() => setReplyTo(null)} className="ml-2 text-red-400/70">×</button>
                </div>
              )}
              <input
                className="aura-input !h-10"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t('feed.commentPlaceholder')}
                maxLength={300}
                data-testid="comment-input"
              />
            </div>
            <button disabled={busy || !text.trim()} className="aura-btn-primary !py-2 !px-3 !text-sm disabled:opacity-50" data-testid="comment-submit">
              <Send size={14} />
            </button>
          </form>
        ) : (
          <div className="border-t border-white/10 px-4 py-3 text-center text-sm text-aura-textDim">{t('feed.signInToComment')}</div>
        )}
      </div>
    </div>
  );
}
